import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import placeholder from "@/assets/placeholder.png?url";

interface AvatarUploadProps {
  userId: string;
  currentPhotoUrl?: string | null;
  userName?: string | null;
  onUploadComplete?: (photoUrl: string) => void;
  size?: "sm" | "md" | "lg";
}

export default function AvatarUpload({ 
  userId, 
  currentPhotoUrl, 
  userName, 
  onUploadComplete,
  size = "md" 
}: AvatarUploadProps) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-12 w-12", 
    lg: "h-24 w-24"
  };

  const userInitials = userName
    ?.split(" ")
    .map((n) => n[0])
    .join("") || "U";

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Vælg venligst et billede");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Billedet må maksimalt være 5MB");
      return;
    }

    setUploading(true);
    
    try {
      // Create unique filename with user folder structure
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;
      
      // Upload to avatars bucket (upsert to replace existing)
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const photoUrl = publicUrlData.publicUrl;

      // Update user profile
      const { error: updateError } = await supabase
        .from('users')
        .update({ photo_url: photoUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      toast.success("Profilbillede opdateret!");
      onUploadComplete?.(photoUrl);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Der skete en fejl ved upload. Prøv igen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center space-x-3">
      <div className="relative">
        <Avatar className={sizeClasses[size]}>
          <AvatarImage 
            src={currentPhotoUrl || placeholder}
            onError={(e) => {
              if (e.currentTarget.src !== placeholder) e.currentTarget.src = placeholder;
            }}
          />
          <AvatarFallback>{userInitials}</AvatarFallback>
        </Avatar>
        
        {size === "lg" && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Camera className="h-3 w-3" />
          </Button>
        )}
      </div>

      {size !== "lg" && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? "Uploader..." : "Skift billede"}
        </Button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}